import {Vec2, vec2, Mat2d} from 'utility/gl-matrix';
import { Polyline } from 'utility/Polyline';

export interface Rectangle {
  origin: Vec2;
  size: Vec2;
}

export function zero(): Rectangle {
  return { origin: vec2.create(), size: vec2.create() };
}

export function from(otherFormat: { minX: number, minY: number, maxX: number, maxY: number }): Rectangle {
  return {
    origin: [otherFormat.minX, otherFormat.minY],
    size: [otherFormat.maxX - otherFormat.minX, otherFormat.maxY - otherFormat.minY],
  };
}

export function toPolyline(rect: Rectangle): Polyline {
  return { points: corners(rect) };
}

export function corners(rect: Rectangle): [Vec2, Vec2, Vec2, Vec2] {
  return [
    rect.origin,
    vec2.fromValues(
      vec2.x(rect.origin),
      vec2.y(rect.origin) + vec2.y(rect.size),
    ),
    vec2.fromValues(
      vec2.x(rect.origin) + vec2.x(rect.size),
      vec2.y(rect.origin),
    ),
    vec2.fromValues(
      vec2.x(rect.origin) + vec2.x(rect.size),
      vec2.y(rect.origin) + vec2.y(rect.size),
    ),
  ];
}

export function toTopBottomLeftRight(rect: Rectangle): {top: number, left: number, bottom: number, right: number} {
  const max = vec2.add(vec2.create(), rect.origin, rect.size);
  const min = rect.origin;

  return {
    left: vec2.x(min),
    top: vec2.y(min),
    right: vec2.x(max),
    bottom: vec2.y(max),
  };
}

export function center(rect: Rectangle, out: Vec2 = vec2.create()): Vec2 {
  vec2.scale(out, rect.size, 0.5);
  vec2.add(out, out, rect.origin);
  return out;
}

export function aabbByTransforming(rect: Rectangle, xf: Mat2d): Rectangle {
  const transformedCorners = corners(rect).map((corner) => vec2.transformMat2d(corner, corner, xf));
  let min = vec2.clone(transformedCorners[0]);
  let max = vec2.clone(transformedCorners[0]);
  for (const corner of transformedCorners) {
    if (vec2.x(corner) < vec2.x(min)) {
      vec2.setX(min, vec2.x(corner));
    }
    if (vec2.y(corner) < vec2.y(min)) {
      vec2.setY(min, vec2.y(corner));
    }
    if (vec2.x(corner) > vec2.x(max)) {
      vec2.setX(max, vec2.x(corner));
    }
    if (vec2.y(corner) > vec2.y(max)) {
      vec2.setY(max, vec2.y(corner));
    }
  }
  return {
    origin: min,
    size: vec2.subtract(vec2.create(), max, min),
  };
}

export function toXYWidthHeight(rect: Rectangle): { x: number, y: number, width: number, height: number } {
  return {
    x: vec2.x(rect.origin),
    y: vec2.y(rect.origin),
    width: vec2.x(rect.size),
    height: vec2.y(rect.size),
  };
}

export function fromXYWidthHeight(rect: { x: number, y: number, width: number, height: number }): Rectangle {
  return {
    origin: vec2.fromValues(rect.x, rect.y),
    size: vec2.fromValues(rect.width, rect.height),
  };
}

export const max = (r: Rectangle): Vec2 => vec2.add(vec2.create(), r.origin, r.size);
export const min = (r: Rectangle): Vec2 => r.origin;

export function intersects(rect1: Rectangle, rect2: Rectangle): boolean {
  const rect1Max = max(rect1);
  const rect2Max = max(rect2);

  if (vec2.x(rect1.origin) >= vec2.x(rect2Max) || vec2.x(rect2.origin) >= vec2.x(rect1Max)) {
    return false;
  }

  if (vec2.y(rect1.origin) >= vec2.y(rect2Max) || vec2.y(rect2.origin) >= vec2.y(rect1Max)) {
    return false;
  }

  return true;
}
